// Admin Households tab — list every household on the instance, drill
// into one to see members + chores, and manage either roster.
//
// Cross-household ops go through admin.* RPCs which bypass the usual
// active-household scoping; the server's admin gate is the only check.
//
// Layout: left rail of households (with counts as eyebrow), right pane
// with two sub-sections — Members and Chores. Picks up the same
// surface conventions as the Manage Calendars dialog so admins don't
// have to learn a third layout.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { confirm } from "../../components/confirm";
import { api, type AdminHousehold, type Chore, type ChoreFrequency, type HouseholdMember } from "../../lib/rpc";

interface AdminHouseholdsTabProps {
  notify: (msg: string) => void;
}

const CHORE_ICONS = [
  "🧺", "🧹", "🧼", "🛏️", "🍽️", "🚮", "🐕", "🐈", "🪴", "🌱",
  "📚", "✏️", "📖", "🎒", "🧸", "🪥", "🚿", "🛁", "🧴", "🧽",
  "🍳", "🥕", "🍎", "🚲", "⚽", "🎨", "🎵", "💪", "🌟", "✨",
];

const FREQUENCIES: { id: ChoreFrequency; label: string }[] = [
  { id: "daily",  label: "Every day" },
  { id: "weekly", label: "Once a week" },
];

export function AdminHouseholdsTab({ notify }: AdminHouseholdsTabProps) {
  const [households, setHouseholds] = useState<AdminHousehold[] | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [members, setMembers] = useState<HouseholdMember[]>([]);
  const [chores, setChores] = useState<Chore[]>([]);
  const [editing, setEditing] = useState<Chore | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  // Inline rename state — keyed by household id so two rows can be
  // edited concurrently without state collisions.
  const [renameDrafts, setRenameDrafts] = useState<Record<number, string>>({});
  // Add-member draft per household.
  const [addMemberDrafts, setAddMemberDrafts] = useState<Record<number, string>>({});

  const selected = households?.find((h) => h.id === selectedId) || null;

  const refreshHouseholds = async () => {
    try {
      const list = await api.adminHouseholdsList();
      setHouseholds(list);
      // Auto-select the first row on initial load so the right pane
      // isn't an empty stare. Sticky thereafter — only flip the
      // selection if the previously-selected household disappeared.
      if (list.length > 0 && (selectedId === null || !list.some((h) => h.id === selectedId))) {
        setSelectedId(list[0].id);
      }
    } catch (e: any) { notify(e?.message || "Couldn't load households"); setHouseholds([]); }
  };

  const refreshSelected = async () => {
    if (!selectedId) { setMembers([]); setChores([]); return; }
    try {
      const [m, c] = await Promise.all([
        api.adminHouseholdMembers(selectedId),
        api.adminChoresList(selectedId),
      ]);
      setMembers(m);
      setChores(c);
    } catch (e: any) {
      notify(e?.message || "Couldn't load household detail");
    }
  };

  useEffect(() => { void refreshHouseholds(); }, []);
  useEffect(() => { void refreshSelected(); }, [selectedId]);

  const onRename = async (h: AdminHousehold) => {
    const draft = (renameDrafts[h.id] ?? "").trim();
    if (!draft || draft === h.name) {
      setRenameDrafts((prev) => { const next = { ...prev }; delete next[h.id]; return next; });
      return;
    }
    try {
      await api.adminHouseholdRename(h.id, draft);
      notify("Renamed");
      setRenameDrafts((prev) => { const next = { ...prev }; delete next[h.id]; return next; });
      await refreshHouseholds();
    } catch (e: any) { notify(e?.message || "rename failed"); }
  };

  const onDelete = async (h: AdminHousehold) => {
    const ok = await confirm({
      title: `Delete "${h.name}"?`,
      message: `Deletes the household and ALL its data — ${h.memberCount} members, ${h.mealPlanCount} meal plan entries, ${h.pantryCount} pantry items, ${h.shoppingCount} shopping list rows, and every chore. This can't be undone.`,
      confirmLabel: "Delete forever",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.adminHouseholdDelete(h.id);
      notify(`Deleted "${h.name}"`);
      if (selectedId === h.id) setSelectedId(null);
      await refreshHouseholds();
    } catch (e: any) { notify(e?.message || "delete failed"); }
  };

  const onAddMember = async () => {
    if (!selectedId) return;
    const draft = (addMemberDrafts[selectedId] ?? "").trim();
    if (!draft) return;
    try {
      await api.adminHouseholdAddMember(selectedId, draft, "member");
      notify(`Added ${draft}`);
      setAddMemberDrafts((prev) => ({ ...prev, [selectedId]: "" }));
      await Promise.all([refreshHouseholds(), refreshSelected()]);
    } catch (e: any) { notify(e?.message || "add failed"); }
  };

  const onRemoveMember = async (m: HouseholdMember) => {
    if (!selected) return;
    const ok = await confirm({
      title: `Remove ${m.userId}?`,
      message: `They lose access to "${selected.name}" but their account stays. Re-add later if needed.`,
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.adminHouseholdRemoveMember(selected.id, m.userId);
      notify("Removed");
      await Promise.all([refreshHouseholds(), refreshSelected()]);
    } catch (e: any) { notify(e?.message || "remove failed"); }
  };

  const onArchiveChore = async (c: Chore) => {
    const ok = await confirm({
      title: `Archive "${c.name}"?`,
      message: "It'll stop showing up on the chart. Past completions stay.",
      confirmLabel: "Archive",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.adminChoreArchive(c.id);
      notify("Archived");
      await refreshSelected();
    } catch (e: any) { notify(e?.message || "archive failed"); }
  };

  return (
    <div className="admin-households">
      <aside className="admin-households-rail">
        <div className="eyebrow" style={{ padding: "8px 12px" }}>
          Households {households ? `(${households.length})` : ""}
        </div>
        <div className="admin-households-list">
          {households === null && <div className="admin-households-empty">loading…</div>}
          {households && households.length === 0 && (
            <div className="admin-households-empty">No households yet.</div>
          )}
          {households?.map((h) => (
            <button
              key={h.id}
              className="admin-households-row"
              data-active={selectedId === h.id}
              type="button"
              onClick={() => setSelectedId(h.id)}
            >
              <span className="admin-households-row-name">{h.name}</span>
              <span className="admin-households-row-meta">
                {h.memberCount}m · {h.mealPlanCount}mp · {h.pantryCount}p · {h.shoppingCount}sl
              </span>
            </button>
          ))}
        </div>
      </aside>

      <div className="admin-households-main">
        {!selected ? (
          <div className="admin-households-empty-pane">
            <Icon name="home" size={32} />
            <p>Pick a household on the left.</p>
          </div>
        ) : (
          <>
            <header className="admin-households-head">
              <input
                className="input admin-households-rename"
                value={renameDrafts[selected.id] ?? selected.name}
                onInput={(e) => setRenameDrafts((prev) => ({ ...prev, [selected.id]: (e.currentTarget as HTMLInputElement).value }))}
                onBlur={() => onRename(selected)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
                  if (e.key === "Escape") setRenameDrafts((prev) => { const n = { ...prev }; delete n[selected.id]; return n; });
                }}
              />
              <button
                className="btn admin-households-delete"
                type="button"
                onClick={() => onDelete(selected)}
              >
                <Icon name="trash" size={13} /> Delete household
              </button>
            </header>

            <section className="admin-households-section">
              <div className="eyebrow">Members ({members.length})</div>
              <ul className="admin-households-members">
                {members.map((m) => (
                  <li key={m.userId} className="admin-households-member">
                    <span className="admin-households-avatar">{m.userId.slice(0, 1).toUpperCase()}</span>
                    <span className="admin-households-uid">{m.userId}</span>
                    <span className="admin-households-role">{m.role}</span>
                    <button
                      className="icon-btn"
                      type="button"
                      title="Remove"
                      onClick={() => onRemoveMember(m)}
                    >
                      <Icon name="x" size={12} />
                    </button>
                  </li>
                ))}
              </ul>
              <div className="admin-households-add">
                <input
                  className="input"
                  placeholder="Add user id"
                  value={addMemberDrafts[selected.id] ?? ""}
                  onInput={(e) => setAddMemberDrafts((prev) => ({ ...prev, [selected.id]: (e.currentTarget as HTMLInputElement).value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") void onAddMember(); }}
                />
                <button
                  className="btn"
                  type="button"
                  onClick={onAddMember}
                  disabled={!(addMemberDrafts[selected.id] ?? "").trim()}
                >
                  <Icon name="plus" size={13} /> Add member
                </button>
              </div>
            </section>

            <section className="admin-households-section">
              <div className="admin-households-section-head">
                <div className="eyebrow">Chores ({chores.length})</div>
                <button className="btn" type="button" onClick={() => setAddOpen(true)}>
                  <Icon name="plus" size={13} /> Add chore
                </button>
              </div>
              {chores.length === 0 ? (
                <div className="admin-households-empty">No chores in this household.</div>
              ) : (
                <ul className="admin-households-chores">
                  {chores.map((c) => (
                    <li key={c.id} className="admin-households-chore">
                      <button
                        className="admin-households-chore-main"
                        type="button"
                        onClick={() => setEditing(c)}
                      >
                        <span className="admin-households-chore-icon">{c.icon || "✅"}</span>
                        <span className="admin-households-chore-body">
                          <span className="admin-households-chore-name">{c.name}</span>
                          <span className="admin-households-chore-meta">
                            {c.assignedToUserId || "(unassigned)"} · ⭐ {c.stars} · {c.frequency}
                            {c.requiresApproval ? " · approval" : ""}
                            {c.pendingApprovals ? ` · ${c.pendingApprovals} pending` : ""}
                          </span>
                        </span>
                      </button>
                      <button
                        className="icon-btn"
                        type="button"
                        title="Archive"
                        onClick={() => onArchiveChore(c)}
                      >
                        <Icon name="trash" size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>

      {addOpen && selected && (
        <AdminChoreForm
          mode="create"
          householdId={selected.id}
          members={members}
          onCancel={() => setAddOpen(false)}
          onSaved={async () => { setAddOpen(false); await refreshSelected(); notify("Added"); }}
          notify={notify}
        />
      )}
      {editing && selected && (
        <AdminChoreForm
          mode="edit"
          householdId={selected.id}
          chore={editing}
          members={members}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await refreshSelected(); notify("Saved"); }}
          notify={notify}
        />
      )}
    </div>
  );
}

interface AdminChoreFormProps {
  mode: "create" | "edit";
  householdId: number;
  chore?: Chore;
  members: HouseholdMember[];
  onCancel: () => void;
  onSaved: () => void;
  notify: (msg: string) => void;
}

function AdminChoreForm({ mode, householdId, chore, members, onCancel, onSaved, notify }: AdminChoreFormProps) {
  const kids = members.filter((m) => m.role === "kid");
  const [name, setName] = useState(chore?.name || "");
  const [icon, setIcon] = useState(chore?.icon || "🧺");
  const [assignedTo, setAssignedTo] = useState(chore?.assignedToUserId || kids[0]?.userId || "");
  const [frequency, setFrequency] = useState<ChoreFrequency>(chore?.frequency || "daily");
  const [stars, setStars] = useState(chore?.stars ?? 1);
  const [requiresApproval, setRequiresApproval] = useState(chore?.requiresApproval ?? false);
  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    const t = name.trim();
    if (!t) { notify("Name required"); return; }
    setSaving(true);
    try {
      if (mode === "create") {
        await api.adminChoreCreate({
          householdId, name: t, icon,
          assignedToUserId: assignedTo || null,
          frequency, stars, requiresApproval,
        });
      } else if (chore) {
        await api.adminChoreUpdate(chore.id, {
          name: t, icon,
          assignedToUserId: assignedTo || null,
          frequency, stars, requiresApproval,
        });
      }
      onSaved();
    } catch (e: any) {
      notify(e?.message || "save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="confirm-bg" onClick={onCancel}>
      <div className="chore-form" onClick={(e) => e.stopPropagation()} role="dialog">
        <header className="chore-form-head">
          <h3>{mode === "create" ? "New chore" : "Edit chore"}</h3>
          <button className="icon-btn" type="button" onClick={onCancel}>
            <Icon name="x" size={12} />
          </button>
        </header>

        <label className="chore-form-field">
          <span className="chore-form-label">Name</span>
          <input
            value={name}
            onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
            placeholder="Make the bed"
            maxLength={80}
            disabled={saving}
          />
        </label>

        <div className="chore-form-field">
          <span className="chore-form-label">Icon</span>
          <div className="chore-form-icons">
            {CHORE_ICONS.map((e) => (
              <button
                key={e}
                type="button"
                className={`chore-form-icon${icon === e ? " is-selected" : ""}`}
                onClick={() => setIcon(e)}
              >{e}</button>
            ))}
          </div>
        </div>

        <label className="chore-form-field">
          <span className="chore-form-label">Who does it</span>
          <select
            value={assignedTo}
            onChange={(e) => setAssignedTo((e.currentTarget as HTMLSelectElement).value)}
            disabled={saving}
          >
            <option value="">(unassigned — anyone)</option>
            {members.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.userId}{m.role === "kid" ? " (kid)" : ""}
              </option>
            ))}
          </select>
        </label>

        <label className="chore-form-field">
          <span className="chore-form-label">How often</span>
          <select
            value={frequency}
            onChange={(e) => setFrequency((e.currentTarget as HTMLSelectElement).value as ChoreFrequency)}
            disabled={saving}
          >
            {FREQUENCIES.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
        </label>

        <label className="chore-form-field">
          <span className="chore-form-label">Stars per completion</span>
          <input
            type="number"
            min={0}
            max={20}
            value={stars}
            onInput={(e) => setStars(Math.max(0, Math.min(20, Number((e.currentTarget as HTMLInputElement).value) || 0)))}
            disabled={saving}
          />
        </label>

        <label className="chore-form-toggle">
          <input
            type="checkbox"
            checked={requiresApproval}
            onChange={(e) => setRequiresApproval((e.currentTarget as HTMLInputElement).checked)}
            disabled={saving}
          />
          <span>Parent approves before stars are granted</span>
        </label>

        <div className="chore-form-actions">
          <button className="chore-form-cancel" type="button" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="chore-form-save" type="button" onClick={onSave} disabled={saving || !name.trim()}>
            {mode === "create" ? "Add chore" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
